import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories, articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';
import type { BreadcrumbItem } from '@/components/base/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const categoryImageMap: Record<string, string> = {
    tutorial: 'https://readdy.ai/api/search-image?query=Modern%20online%20learning%20and%20tutorial%20concept%20with%20laptop%20screen%20showing%20clean%20step%20by%20step%20guide%2C%20warm%20lighting%2C%20minimalist%20workspace%2C%20professional%20editorial%20photography&width=800&height=400&seq=cat-tutorial&orientation=landscape',
    updates: 'https://readdy.ai/api/search-image?query=Software%20update%20and%20version%20release%20concept%20with%20clean%20interface%20mockup%2C%20modern%20tech%20aesthetic%2C%20gradient%20background%2C%20professional%20design&width=800&height=400&seq=cat-updates&orientation=landscape',
    help: 'https://readdy.ai/api/search-image?query=Customer%20support%20and%20help%20center%20concept%20with%20clean%20workspace%2C%20warm%20lighting%2C%20professional%20setting%2C%20friendly%20atmosphere&width=800&height=400&seq=cat-help&orientation=landscape',
    cases: 'https://readdy.ai/api/search-image?query=User%20case%20studies%20and%20success%20stories%20concept%20with%20clean%20modern%20office%2C%20professional%20team%20collaboration%2C%20warm%20lighting&width=800&height=400&seq=cat-cases&orientation=landscape',
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - ${siteConfig.siteName}`,
          description: currentCategory.description,
          url: `${siteConfig.siteUrl}/category/${activeCategory}`,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: breadcrumbItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.label,
            item: item.href ? `${siteConfig.siteUrl}${item.href}` : undefined,
          })),
        }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <h2 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                    分类目录
                  </h2>
                  <nav className="flex flex-col gap-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        onClick={() => setActiveCategory(cat.slug)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                          activeCategory === cat.slug
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                        }`}
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        {cat.name}
                        <span className={`ml-auto text-xs ${
                          activeCategory === cat.slug ? 'text-background-50/70' : 'text-foreground-400'
                        }`}>
                          {cat.articleCount}
                        </span>
                      </Link>
                    ))}
                  </nav>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="mb-8">
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {currentCategory.description}
                  </p>
                </div>

                {categoryArticles.length > 0 && (
                  <img
                    src={categoryImageMap[activeCategory] || categoryImageMap.tutorial}
                    alt={currentCategory.name}
                    className="w-full h-48 md:h-64 object-cover rounded-xl mb-8"
                  />
                )}

                <div className="flex flex-col gap-4">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="group block p-5 md:p-6 rounded-xl bg-background-50 hover:bg-background-200/60 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-foreground-500">{article.date}</span>
                            <span className="text-xs text-foreground-500">&middot; {article.readTime}阅读</span>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {article.tags.map((tag) => (
                                <span key={tag} className="text-xs text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded-full">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors mt-1 flex-shrink-0"></i>
                      </div>
                    </Link>
                  ))}

                  {categoryArticles.length === 0 && (
                    <div className="text-center py-16">
                      <div className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                      </div>
                      <p className="text-foreground-500">该分类下暂无文章</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}